/**
 * scoreSiteFinancial — deterministic verdict banding for the Financial
 * Viability Report (FIN-1, wf w7nuyquwd).
 *
 * The financial body is computed LIVE from the deterministic project-finance
 * engine (`projectFinance.recompute` + `finDecision/*`). This module does NOT
 * re-derive any LCOE / NPV / IRR / Monte-Carlo math — it ONLY BANDS the already-
 * computed engine outputs into a 0-100 `overallScore` (the value
 * `tierFromScore('financial', …)` reads) plus a Viable / Conditional /
 * Unfinanceable tier.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * BASIS DISCIPLINE (review 2026-08-19, decision Q1 — THE rule of this module)
 * ─────────────────────────────────────────────────────────────────────────────
 * The verdict bands the PROJECT's own economics — the site financial model
 * (CAPEX / OPEX / financing / PPA the sponsor declared). The pathway engine
 * (`finDecision/*`) prices BENCHMARK TECHNOLOGY options against a flat
 * value-of-served-load credit; its NPV is negative BY CONSTRUCTION for most
 * presets (a firm 24/7 supply costs more than the $/MWh it is credited), so its
 * P(NPV>0) is NOT a statement about this project and MUST NEVER band this
 * verdict. It is carried as INFORMATIONAL rationale only.
 *
 * Before this rule existed, the same financial model banded green or red purely
 * on which pathway happened to sit first in the comparison list.
 *
 * BANDING:
 *   none   / —             — inputs insufficient (NPV unavailable), OR the
 *                            engine outputs are not internally consistent
 *                            (implausibility guard below). NO fabricated
 *                            downgrade: a missing/incoherent input yields NO
 *                            score so the report tier is 'none' (not a verdict
 *                            floor contributor), keeping a prior band stable.
 *   red    / Unfinanceable — NPV < 0, OR IRR materially below hurdle, OR a
 *                            PROJECT-basis P(NPV>0) < 0.3.
 *   amber  / Conditional   — NPV ≥ 0 but the margin of safety is thin: IRR
 *                            clears the hurdle by less than
 *                            `IRR_THIN_HEADROOM_PCT`, payback lands within
 *                            `PAYBACK_THIN_MARGIN_FRACTION` of design life, a
 *                            PROJECT-basis P(NPV>0) sits in [0.3, 0.5), or NPV
 *                            is exactly break-even.
 *   green  / Viable        — NPV > 0 with real headroom on every known lever.
 *
 * WHY "thin headroom" and not a second IRR gate: for a conventional cashflow
 * discounted at the same rate, NPV ≥ 0 ⟺ IRR ≥ WACC — a pass/fail IRR test
 * against a WACC hurdle is arithmetically the SAME test as the NPV gate and can
 * never produce a distinct band. The decision-useful signal is the MARGIN, so
 * Conditional is defined by how thin that margin is. An explicit
 * `hurdleRateOverride` (a target return above WACC) still makes the IRR test
 * bite independently — that is the case the material-shortfall red trigger
 * exists for.
 *
 * VERDICT-STABILITY: the function is PURE and deterministic over its inputs.
 * The downstream firm-capacity (capTierByFirmCapacity) + EA8 evidence-tier caps
 * stack on top in `getDisplayVerdict` (monotonic-down only).
 */

/** Where a probability-of-positive-NPV figure was measured. */
export type PPositiveNpvBasis = 'project' | 'pathway';

/** Engine-output snapshot the banding reads. All optional — missing → 'none'. */
export interface FinancialScoreInputs {
  /** Project NPV (USD) from the engine. The PRIMARY gate. */
  npvUsd: number | null | undefined;
  /** Project IRR (percent, e.g. 12 = 12%). */
  projectIrrPct: number | null | undefined;
  /**
   * Hurdle / target IRR (percent). Resolution order at the call site:
   * preferences.hurdleRateOverride → engine WACC / discount rate. When the
   * hurdle is unavailable the IRR lever is treated as NEUTRAL (neither
   * marginal nor failing) rather than fabricated.
   */
  hurdleRatePct: number | null | undefined;
  /** Simple payback (years). */
  paybackYears: number | null | undefined;
  /** Project design life (years) — payback is compared against this. */
  projectLifeYears: number | null | undefined;
  /** Monte-Carlo probability of a positive NPV in [0, 1]. */
  pPositiveNpv: number | null | undefined;
  /**
   * WHAT `pPositiveNpv` MEASURES. Only `'project'` (an uncertainty band around
   * THIS project's own cashflows) may band the verdict. `'pathway'` — the
   * benchmark-technology comparison band — is informational ONLY: it is quoted
   * in the rationale and never sets a band or a binding constraint. Absent is
   * treated as `'pathway'` (the conservative reading: an unlabelled band is not
   * known to be project-basis).
   */
  pPositiveNpvBasis?: PPositiveNpvBasis | null;
  /**
   * IMPLAUSIBILITY — set by the caller when the CAPEX basis and the revenue
   * basis are not the same plant: e.g. only Phase 1 is priced but the model
   * earns revenue on the ultimate campus nameplate. The resulting NPV/IRR are
   * arithmetic nonsense (Holly Ridge: $240M of CAPEX against 1,750 MW of
   * revenue → IRR 697%), so the verdict is WITHHELD rather than banded green.
   */
  capexBasisMismatch?: boolean;
  /** Human-readable description of the basis mismatch, for the rationale. */
  capexBasisNote?: string;
}

export type FinancialBand = 'green' | 'amber' | 'red';

export interface FinancialScoreResult {
  /**
   * 0-100 deterministic score for `report.overallScore`. `null` when inputs are
   * insufficient or incoherent — `tierFromScore` then returns 'none'. Banded so
   * that the uniform `tierFromScore` thresholds (≥67 green, ≥34 amber, else red)
   * reproduce `band` exactly.
   */
  score: number | null;
  /** Banded verdict, or null when insufficient / withheld. */
  band: FinancialBand | null;
  /** Auditable reasons the band landed where it did. */
  rationale: string[];
  /** The binding constraints that held the band down (empty when green). */
  bindingConstraints: string[];
  /**
   * True when the band was WITHHELD because the engine outputs are not
   * internally consistent (as opposed to simply missing). Lets the report say
   * "we can compute a number but it would be misleading" instead of "no data".
   */
  withheldForImplausibility?: boolean;
}

/**
 * How far below the hurdle IRR is treated as "materially below" (a red
 * trigger) vs merely "marginal" (an amber trigger). 1.5 percentage points.
 */
const IRR_MATERIAL_SHORTFALL_PCT = 1.5;

/**
 * How much headroom over the hurdle an IRR needs before the project reads as
 * comfortably financeable rather than Conditional. 1.5 percentage points —
 * symmetric with the material-shortfall band on the downside.
 */
const IRR_THIN_HEADROOM_PCT = 1.5;

/**
 * Payback within this fraction of the design life reads as "no margin" — the
 * project only repays in its final years, so any life or availability slip
 * pushes it past breakeven. 10% of life.
 */
const PAYBACK_THIN_MARGIN_FRACTION = 0.1;

/**
 * IMPLAUSIBILITY CAPS — engine outputs beyond these are not "excellent
 * economics", they are a sign the CAPEX basis and revenue basis disagree.
 * Utility-scale generation does not return 60%+ unlevered IRR or repay its
 * capital inside a year; a model that says so is mis-specified.
 */
const IMPLAUSIBLE_IRR_PCT = 60;
const IMPLAUSIBLE_PAYBACK_YEARS = 1;

/** Banded score centroids — chosen to fall inside the uniform tier bands. */
const SCORE_GREEN = 82; // ≥ 67 → green
const SCORE_AMBER = 50; // [34, 66] → amber
const SCORE_RED = 18; //   < 34  → red

function isNum(v: number | null | undefined): v is number {
  return typeof v === 'number' && Number.isFinite(v);
}

/** Withheld result — no score, no band, no fabricated downgrade. */
function withheld(
  rationale: string[],
  bindingConstraints: string[],
  forImplausibility: boolean,
): FinancialScoreResult {
  return {
    score: null,
    band: null,
    rationale,
    bindingConstraints,
    ...(forImplausibility ? { withheldForImplausibility: true } : {}),
  };
}

/**
 * Band the deterministic project-finance engine outputs into a financial
 * verdict. Pure / deterministic. Returns `{ score: null, band: null }` when
 * inputs are insufficient (NPV unavailable) or incoherent (implausibility).
 */
export function scoreSiteFinancial(inputs: FinancialScoreInputs): FinancialScoreResult {
  const {
    npvUsd,
    projectIrrPct,
    hurdleRatePct,
    paybackYears,
    projectLifeYears,
    pPositiveNpv,
    capexBasisMismatch,
    capexBasisNote,
  } = inputs;

  // INSUFFICIENT INPUTS — NPV is the primary gate. Without it there is no
  // defensible band; return no score so the report tier resolves to 'none'.
  // (Never a fabricated downgrade.)
  if (!isNum(npvUsd)) {
    return withheld(
      ['Financial inputs insufficient — NPV not available from the project-finance engine.'],
      [],
      false,
    );
  }

  // ── IMPLAUSIBILITY GUARD — run BEFORE banding. ───────────────────────────
  // An internally inconsistent model must not be graded; a green verdict on
  // nonsense is worse than no verdict.
  const implausible: string[] = [];
  if (capexBasisMismatch === true) {
    implausible.push(
      capexBasisNote
        ?? 'CAPEX basis and revenue basis describe different plant — priced CAPEX does not cover the capacity earning revenue.',
    );
  }
  if (isNum(projectIrrPct) && projectIrrPct > IMPLAUSIBLE_IRR_PCT) {
    implausible.push(
      `Project IRR (${projectIrrPct.toFixed(1)}%) exceeds the ${IMPLAUSIBLE_IRR_PCT}% plausibility ceiling for utility-scale generation — check that CAPEX covers the full revenue-earning capacity.`,
    );
  }
  if (isNum(paybackYears) && paybackYears < IMPLAUSIBLE_PAYBACK_YEARS) {
    implausible.push(
      `Payback (${paybackYears.toFixed(2)} yr) is under ${IMPLAUSIBLE_PAYBACK_YEARS} year — implausible for a capital project; the CAPEX basis is likely incomplete.`,
    );
  }
  if (implausible.length > 0) {
    return withheld(
      [
        'Financial verdict WITHHELD — the engine outputs are not internally consistent, so banding them would be misleading.',
        ...implausible,
      ],
      implausible,
      true,
    );
  }

  const rationale: string[] = [];
  const bindingConstraints: string[] = [];

  // P(NPV>0) is only a verdict lever on the PROJECT basis (see module header).
  // An unlabelled basis is read conservatively as 'pathway' (informational).
  const pPosKnown = isNum(pPositiveNpv);
  const pPosIsProjectBasis = pPosKnown && inputs.pPositiveNpvBasis === 'project';
  const pPosPct = pPosKnown ? (pPositiveNpv * 100).toFixed(0) : null;

  // ── RED triggers (any one → Unfinanceable) ────────────────────────────────
  // 1. NPV < 0.
  if (npvUsd < 0) {
    bindingConstraints.push('Negative project NPV');
    rationale.push(`NPV is negative ($${(npvUsd / 1_000_000).toFixed(1)}M) — the build does not clear its cost of capital.`);
  }
  // 2. IRR materially below the hurdle (only when both are known). Bites
  //    independently of the NPV gate when the hurdle is an explicit target
  //    return above WACC.
  const hurdleKnown = isNum(hurdleRatePct);
  const irrKnown = isNum(projectIrrPct);
  const irrShortfallPct = hurdleKnown && irrKnown ? hurdleRatePct - projectIrrPct : null;
  const irrMateriallyBelow = irrShortfallPct != null && irrShortfallPct > IRR_MATERIAL_SHORTFALL_PCT;
  if (irrMateriallyBelow) {
    bindingConstraints.push('IRR materially below hurdle');
    rationale.push(
      `Project IRR (${projectIrrPct!.toFixed(1)}%) is more than ${IRR_MATERIAL_SHORTFALL_PCT} pts below the ${hurdleRatePct!.toFixed(1)}% hurdle.`,
    );
  }
  // 3. PROJECT-basis P(positive NPV) < 0.3.
  if (pPosIsProjectBasis && pPositiveNpv! < 0.3) {
    bindingConstraints.push('Low probability of a positive NPV');
    rationale.push(`Monte-Carlo P(NPV>0) for this project is ${pPosPct}% (< 30%).`);
  }

  if (bindingConstraints.length > 0) {
    if (pPosKnown && !pPosIsProjectBasis) {
      rationale.push(pathwayBandNote(pPosPct!));
    }
    return {
      score: SCORE_RED,
      band: 'red',
      rationale,
      bindingConstraints,
    };
  }

  // ── At this point NPV ≥ 0 and no red trigger fired. ───────────────────────
  // Count THIN-MARGIN levers to decide green vs amber. These are margin
  // measurements, not a second pass/fail of the NPV test (see module header).
  const marginalLevers: string[] = [];

  // IRR headroom over the hurdle is thin (including "just under", which the
  // material-shortfall trigger above did not catch).
  if (irrShortfallPct != null && irrShortfallPct > -IRR_THIN_HEADROOM_PCT) {
    marginalLevers.push(
      irrShortfallPct > 0
        ? `IRR (${projectIrrPct!.toFixed(1)}%) is just under the ${hurdleRatePct!.toFixed(1)}% hurdle.`
        : `IRR (${projectIrrPct!.toFixed(1)}%) clears the ${hurdleRatePct!.toFixed(1)}% hurdle by less than ${IRR_THIN_HEADROOM_PCT} pts — thin margin of safety.`,
    );
  }

  // Payback lands at or near the end of design life (only when both known).
  const paybackKnown = isNum(paybackYears);
  const lifeKnown = isNum(projectLifeYears);
  if (paybackKnown && lifeKnown && projectLifeYears > 0) {
    if (paybackYears > projectLifeYears) {
      marginalLevers.push(
        `Payback (${paybackYears.toFixed(1)} yr) exceeds the ${projectLifeYears.toFixed(0)} yr design life.`,
      );
    } else if (paybackYears > projectLifeYears * (1 - PAYBACK_THIN_MARGIN_FRACTION)) {
      marginalLevers.push(
        `Payback (${paybackYears.toFixed(1)} yr) lands in the final ${(PAYBACK_THIN_MARGIN_FRACTION * 100).toFixed(0)}% of the ${projectLifeYears.toFixed(0)} yr design life — no schedule margin.`,
      );
    }
  }

  // PROJECT-basis probability of positive NPV in the conditional band.
  if (pPosIsProjectBasis && pPositiveNpv! >= 0.3 && pPositiveNpv! < 0.5) {
    marginalLevers.push(`Monte-Carlo P(NPV>0) for this project is ${pPosPct}% (below the 50% confidence floor).`);
  }

  // GREEN — every defensible lever clears its floor with headroom.
  if (marginalLevers.length === 0 && npvUsd > 0) {
    rationale.push(`Positive NPV ($${(npvUsd / 1_000_000).toFixed(1)}M); all financial levers clear their thresholds with margin.`);
    if (irrKnown && hurdleKnown) {
      rationale.push(
        `IRR (${projectIrrPct!.toFixed(1)}%) exceeds the ${hurdleRatePct!.toFixed(1)}% hurdle by ${(-irrShortfallPct!).toFixed(1)} pts.`,
      );
    }
    if (pPosIsProjectBasis) rationale.push(`Monte-Carlo P(NPV>0) for this project is ${pPosPct}% (≥ 50%).`);
    else if (pPosKnown) rationale.push(pathwayBandNote(pPosPct!));
    return {
      score: SCORE_GREEN,
      band: 'green',
      rationale,
      bindingConstraints: [],
    };
  }

  // AMBER — NPV ≥ 0 but at least one lever is thin (or NPV is exactly
  // break-even). Conditional.
  if (marginalLevers.length === 0 && npvUsd === 0) {
    marginalLevers.push('NPV is break-even ($0) — no margin of safety.');
  }
  rationale.push(`NPV is non-negative but the verdict is held to Conditional by ${marginalLevers.length} thin lever${marginalLevers.length === 1 ? '' : 's'}.`);
  rationale.push(...marginalLevers);
  if (pPosKnown && !pPosIsProjectBasis) rationale.push(pathwayBandNote(pPosPct!));
  return {
    score: SCORE_AMBER,
    band: 'amber',
    rationale,
    bindingConstraints: marginalLevers,
  };
}

/**
 * The informational sentence for a PATHWAY-basis probability. Explicitly says
 * what the number is and is not, so a reader never mistakes a benchmark-
 * technology band for a statement about this project's financing risk.
 */
function pathwayBandNote(pctText: string): string {
  return `For reference, the benchmark pathway comparison shows P(NPV>0) of ${pctText}% on its own value-of-served-load basis; that band prices technology options, not this project's declared economics, and does not affect this verdict.`;
}
